import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from '../../contexts/TranslationContext';
import SelectionFormField from '../form/fields/selection';
import {
    MutableRefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { FormChoices } from '../../api/types/form';
import Button from '@mui/material/Button';
import useApi from '../../api';
import { Paper, Stack, Typography } from '@mui/material';
import { TranslateDialogRef } from './TranslateDialog';
import { LoadingOutlined } from '@ant-design/icons';

type DeeplLangArrayType = Array<{ name: string; code: string }>;

type TranslateObjState = {
    text?: string;
    translatedText?: string;
    sourceLocale?: string;
    targetLocale?: string;
};

type ModalTranslationLanguageDialogProps = {
    dialogRef: MutableRefObject<TranslateDialogRef | undefined>;
};

export default function ModalTranslationLanguageDialog({
    dialogRef,
}: ModalTranslationLanguageDialogProps) {
    const saveCallbackRef = useRef<Function>();

    const { cms } = useApi();
    const { translate } = useTranslation();

    const [open, setOpen] = useState(false);

    const [loading, setLoading] = useState(false);
    const [translateObj, setTranslateObj] = useState<TranslateObjState>({
        text: '',
        translatedText: undefined,
        sourceLocale: undefined,
        targetLocale: 'de',
    });
    const [supportedLanguages, setSupportedLanguages] =
        useState<DeeplLangArrayType>([]);

    const loadLanguages = () => {
        cms.getSupportedLanguages()
            .then(response => {
                if (response && !response.error) {
                    setSupportedLanguages(response.languages);
                }
            })
            .catch(console.log);
    };

    const translateModalContent = () => {
        if (translateObj.targetLocale && translateObj.text) {
            setLoading(true);
            cms.translateAnyText({
                targetLocale: translateObj.targetLocale.toLowerCase(),
                sourceLocale: translateObj.sourceLocale?.toLowerCase(),
                text: translateObj.text,
            })
                .then(response => {
                    if (
                        response &&
                        !response.error &&
                        translateObj.targetLocale
                    ) {
                        saveCallbackRef.current?.(
                            response.translated_text,
                            translateObj.targetLocale
                                .substring(0, 2)
                                .toLowerCase(),
                        );
                        handleClose();
                    }
                    setLoading(false);
                })
                .catch(() => setLoading(false));
        }
    };

    useEffect(() => {
        if (dialogRef) {
            dialogRef.current = {
                open: () => {
                    loadLanguages();
                    setOpen(true);
                },
                close: () => setOpen(false),
                setTranslateObj: newValues =>
                    setTranslateObj({ ...translateObj, ...newValues }),
                registerUpdateCallback: (callback: Function) =>
                    (saveCallbackRef.current = callback),
            };
        }
    }, [dialogRef]);

    const handleClose = () => {
        setOpen(false);
    };

    const buildChoices = useCallback((): FormChoices => {
        const response: FormChoices = {};
        for (const lang of supportedLanguages) {
            response[lang.name] = lang.code;
        }
        return response;
    }, [supportedLanguages]);

    return (
        <Dialog open={open} maxWidth="sm" fullWidth onClose={handleClose}>
            <DialogTitle variant="h5">
                {translate('form.general.field_context.translate_val')}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={1}>
                    <Typography color="text.secondary">
                        Es wird automatisch ein neuer loakalisierter Eintrag mit
                        folgender Sprache erzeugt:
                    </Typography>
                    <Paper
                        sx={{
                            p: 2,
                            background: theme => theme.palette.background.paper,
                        }}>
                        <SelectionFormField
                            type="choice"
                            name="targetLocale"
                            label="form.translate.target_locale"
                            required
                            value={translateObj.targetLocale}
                            onChange={newValue =>
                                setTranslateObj({
                                    ...translateObj,
                                    targetLocale: newValue,
                                })
                            }
                            choices={buildChoices()}
                        />
                    </Paper>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {translate('form.general.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={translateModalContent}
                    sx={{ minHeight: 40 }}
                    disabled={loading || !Boolean(translateObj.targetLocale)}>
                    {loading ? (
                        <LoadingOutlined />
                    ) : (
                        translate('form.general.field_context.translate_val')
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
